import {GraphQL} from "../graphql"
import {id} from "../tsutil"
import {ModalStore, WithModalStore} from "../App"
import {BookingWizardWrapper, MapContentParams} from "./BookingWizardWrapper"
import {Divider, Form, Header, Icon, Loader, Message} from "semantic-ui-react"
import {
  BookingDetails,
  BookingDetailsState,
  bookingDetailsStateToBookingDetails,
  detailsEditing,
  detailsValid,
  MapState,
} from "./BookingDetails"
import {errorUnionToEither, OrError} from "../graphql/errors"
import {
  BookerFragFragment,
  BookingGutsFragment,
  BookingStatus,
  DefaultLocale,
  SetBookingDetails,
  SetBookingDetailsMutationVariables,
} from "../graphql/queries"
import * as moment from "moment"
import * as React from "react"
import {BookerPickerSection} from "./BookingBookerPickerSection"
import {
  bookingCancelledMsg,
  bookingHiddenMsg,
  bookingInvoicedMsg,
  bookingNotesOnlyMsg,
  bookingTimeTooClose,
} from "../model/time"
import {StateContainer} from "./StateContainer"
import {bookingToBookingStatusVariables} from "../model/booking"
import {Link} from "react-router-dom"
import {refetchQueries} from "./BookingWizard"
import {BookingButtons} from "./BookingButtons"
import {DriverSection} from "./BookingDriverSection"
import {BookingPricingSection} from "./PricingSection"
import {NotesSection} from "./BookingNotesSection"
import {DriverSelectSection} from "./BookingDriverSelectSection"
import {emptyAirportState} from "./AirportStop"
import {BookingButtonsTop} from "./BookingButtonsTop"
import {BookingWizardDistance} from "./BookingWizardDistance"
import {PromotionsSection} from "./Promotions"
import {PaymentDetailsSection} from "./PaymentDetails"
import {emptyPassenger} from "../model/passenger/passenger"

interface OuterProps extends WithModalStore {
  booking: BookingGutsFragment
  booker: BookerFragFragment
}

export interface SectionProps extends OuterProps {
  disabled: boolean
  editing: boolean
  setEditing: (editing: boolean) => void
  mapParams: MapContentParams
}

type Section =
  | "details"
  | "notes"
  | "payment"
  | "driver"
  | "driverNotes"
  | "pricing"
  | "booker"
  | "promotions"

interface State {
  openSection: Section | null
}

export function isBookingPayer(booking: BookingGutsFragment, booker: BookerFragFragment) {
  return booking.paymentLink !== null && booking.paymentLink.bookerId === booker.id
}

export class UpdateBookingWizard extends React.Component<OuterProps, State> {
  constructor(props: OuterProps) {
    super(props)
    this.state = {
      openSection: this.defaultOpenSection(),
    }
  }

  defaultOpenSection(): Section | null {
    const {booking} = this.props
    return this.justDriver() && !booking.isComplete ? "driverNotes" : null
  }

  isBooker(): boolean {
    const {booking, booker} = this.props
    return Boolean(booking.booker && booking.booker.id === booker.id)
  }

  justDriver(): boolean {
    const {
      booking,
      booker,
      booker: {isAdmin},
    } = this.props
    return this.isDriver() && !(isAdmin || this.isBooker() || isBookingPayer(booking, booker))
  }

  isDriver(): boolean {
    const {booking, booker} = this.props
    // A "safe navigation operator" would be handy here
    return (
      booking.driver !== null &&
      booking.driver.booker !== null &&
      booking.driver.booker.id === booker.id
    )
  }

  render() {
    return (
      <GraphQL.Query
        query={DefaultLocale}
        variables={{}}
        render={({data}) => {
          if (data) {
            const booker = this.props.booking.booker
            const booking = this.props.booking
            return <>
              <Header as="h1">
                Booking <Link to={"/booking/"+booking.number}>#{booking.number}</Link>
              </Header>
              <BookingWizardWrapper
                defaultState={{
                  mapJourney: this.props.booking.journey,
                  mapLocale: booker ? booker.defaultLocale : data.defaultLocale,
                }}
                render={this.renderForm}
              />
            </>
          }
          return <Loader active />
        }}
      />
    )
  }

  renderForm = (mapParams: MapContentParams) => {
    const now = moment()
    const {booking, booker, modalStore} = this.props
    const {openSection} = this.state
    const {isAdmin} = booker
    const {invoice} = booking

    const isBooker = this.isBooker()
    const isPayer = isBookingPayer(booking, booker)
    const isDriver = this.isDriver()
    const justDriver = this.justDriver()
    const isTooClose = bookingTimeTooClose(booking.status, now)
    const {isCancelled, isComplete, isHidden} = booking
    const isInvoiced = invoice !== null
    const isInvoicedOrHidden = isInvoiced || booking.isHidden
    const hasDriver = booking.driver !== null

    // The first condition here ensures that if you are already editing the "details" section when
    // isTooClose becomes true you can keep editing them (which just amounts to hitting Reset).
    const canEditDetails =
      openSection === "details" ||
      (((isBooker && !isTooClose) || isAdmin) && !isCancelled && !isInvoicedOrHidden)
    const canEditPayment = (isAdmin || isBooker || isPayer) && !isInvoicedOrHidden
    const canEditNotes = (isAdmin || isBooker) && !isInvoicedOrHidden && !isCancelled
    const canEditDriverNotes = (isAdmin || isDriver) && !isInvoicedOrHidden && !isCancelled
    const canEditDriver = isAdmin && !isInvoicedOrHidden && !isCancelled && !isComplete
    const canEditBooker = isAdmin && !isInvoicedOrHidden && !isCancelled
    const canEditPrice = isAdmin && !isHidden
    const canEditPromotions = canEditNotes

    const sectionProps = (section: Section, editable: boolean = true): SectionProps => {
      const {openSection} = this.state
      return id({
        ...this.props,
        mapParams,
        disabled: (openSection !== null && openSection !== section) || !editable,
        editing: openSection === section,
        setEditing: (editing: boolean): void => {
          this.setState(({openSection}) => {
            if (editing || openSection === section) {
              mapParams.setMapState(null)
              return {openSection: editing ? section : this.defaultOpenSection()}
            } else {
              // No change
              return {openSection}
            }
          })
        },
      })
    }

    return (
      <div>
        <Form error warning success>
          {(isInvoiced && (
            <Message
              error
              content={
                <>
                  <p>{bookingInvoicedMsg}</p>
                  {invoice &&
                    (invoice.xeroInvoiceReady ? (
                      <Link to={"/invoice/" + invoice.id}>{invoice.number}</Link>
                    ) : (
                      <p>The Xero invoice is being prepared...</p>
                    ))}
                </>
              }
            />
          )) ||
            (isCancelled && <Message error content={bookingCancelledMsg} />) ||
            (isHidden && <Message error content={bookingHiddenMsg} />) ||
            (isBooker && !canEditDetails && <Message warning content={bookingNotesOnlyMsg} />)}

          <BookingButtonsTop booking={booking} booker={booker} disabled={openSection !== null} />
          {(!isBooker || isAdmin) && (
            <BookerPickerSection {...sectionProps("booker", canEditBooker)} />
          )}
        </Form>
        {isAdmin && <DriverSelectSection {...sectionProps("driver", canEditDriver)} />}
        {(isAdmin || isDriver) && hasDriver ? (
          <DriverNotesSection {...sectionProps("driverNotes", canEditDriverNotes)} />
        ) : null}
        <DetailsSection {...sectionProps("details", canEditDetails)} />
        <NotesSection {...sectionProps("notes", canEditNotes)} />
        {isAdmin && (
          <>
            <Divider />
            <Form>
              <BookingWizardDistance journey={booking.journey} disabled={openSection !== null} />
            </Form>
          </>
        )}
        {!justDriver && (
          <>
            <BookingPricingSection {...sectionProps("pricing", canEditPrice)} />
            <PaymentDetailsSection {...sectionProps("payment", canEditPayment)} />
            <PromotionsSection {...sectionProps("promotions", canEditPromotions)} />
            <Divider />
          </>
        )}
        <BookingButtons
          booking={booking}
          booker={booker}
          disabled={openSection !== null}
          modalStore={modalStore}
        />
      </div>
    )
  }
}

export interface UpdatableFormSectionRenderParams<State> {
  state: State
  mapState: MapState<State> | null
}

interface UpdateFormSectionProps<State> extends SectionProps {
  header: string
  state: State
  render: (params: UpdatableFormSectionRenderParams<State>) => React.ReactNode
}

interface SaveResetButtonsProps extends SectionProps {
  onSave: null | (() => Promise<boolean>)
}

export function SaveResetButtons(props: SaveResetButtonsProps) {
  const {disabled, onSave, setEditing, modalStore} = props
  return (
    <>
      <Form.Button
        positive
        disabled={disabled || !onSave}
        onClick={(e) => {
          modalStore.handleActionThrow(async () => {
            e.preventDefault()
            if (!disabled && onSave) {
              if (await onSave()) {
                setEditing(false)
              }
            }
          })
        }}
        content="Submit"
      />
      <Form.Button
        disabled={disabled}
        content="Reset"
        onClick={(e) => {
          e.preventDefault()
          if (!disabled) {
            setEditing(false)
          }
        }}
      />
    </>
  )
}

export function UpdatableFormSection<State>(props: UpdateFormSectionProps<State>) {
  const {editing, header, state, setEditing, render, disabled} = props

  return (
    <>
      <Header as="h3" dividing>
        {/* The div is needed to avoid ".header > .icon" rules */}
        <div>
          {/* Maybe this needs to be a button? */}
          <Icon
            link={!disabled}
            style={{float: "right", marginRight: "0px"}}
            name={editing ? "close" : "edit"}
            disabled={disabled}
            onClick={() => {
              if (!disabled) {
                setEditing(!editing)
              }
            }}
            color="grey"
          />
          {header}
        </div>
      </Header>
      {editing ? (
        <StateContainer initialState={state} render={render} />
      ) : (
        render({state, mapState: null})
      )}
    </>
  )
}

interface DriverNotesSectionState {
  driverNotes: string
}

function DriverNotesSection(props: SectionProps) {
  const state: DriverNotesSectionState = {
    driverNotes: props.booking.driverNotes,
  }

  return (
    <UpdatableFormSection
      {...props}
      header="Driver Info"
      state={state}
      render={({state, mapState}) => {
        return <DriverSection {...props} state={state} mapState={mapState} />
      }}
    />
  )
}

interface DetailsSectionProps extends SectionProps {
  mapParams: MapContentParams
}

function DetailsSection(props: DetailsSectionProps) {
  const isAdmin = props.booker.isAdmin
  const state: BookingDetailsState = id({
    ...props.booking,
    passengerCount: props.booking.passengerCount.toString(),
    editingDate: false,
    initialPassenger: emptyPassenger(),
    editingPassengerIndex: null,
    editingPassenger: false,
    locale: props.booker.defaultLocale,
    airportDropoff: props.booking.airportDropoff || emptyAirportState(),
    airportPickup: props.booking.airportPickup || emptyAirportState(),
  })
  return (
    <UpdatableFormSection
      {...props}
      state={state}
      header="Booking Details"
      render={({state, mapState}) => {
        return (
          <GraphQL.Query
            query={BookingStatus}
            variables={bookingToBookingStatusVariables(state)}
            render={({data, loading}) => {
              if (!data) {
                return <Loader active />
              }
              const stateValid = detailsValid(state, data.bookingStatus, isAdmin)
              const stateLoading = loading
              const stateEditing = detailsEditing(state)
              const enableSave = stateValid && !stateLoading && !stateEditing
              // It is important that everywhere use mapState2 instead of mapState, otherwise
              // the directions map wont be updated with changes to the journey/locale.
              const mapState2: MapState<BookingDetailsState> | null =
                mapState &&
                ((fn) => {
                  mapState((state) => {
                    state = fn(state)
                    props.mapParams.setMapState({
                      mapJourney: state.journey,
                      mapLocale: props.booker.defaultLocale,
                    })
                    return state
                  })
                })
              const optimizeWaypoints =
                mapState2 &&
                (() => {
                  props.modalStore.handleActionThrow(async () => {
                    const waypoints = await props.mapParams.optimizeMapJourney()
                    if (waypoints) {
                      mapState2((state) => {
                        return {...state, journey: {...state.journey, waypoints}}
                      })
                    }
                  })
                })
              return (
                <Form>
                  <BookingDetails
                    {...props}
                    disabled={props.disabled || stateEditing}
                    forBooker={props.booking.booker}
                    isAdmin={props.booker.isAdmin}
                    bookingStatus={data.bookingStatus}
                    bookingStatusLoading={loading}
                    state={state}
                    mapState={mapState2}
                    onOptimizeWaypoints={optimizeWaypoints}
                  />
                  {mapState2 && (
                    <Form.Group>
                      <SaveResetButtons
                        {...props}
                        onSave={
                          mapState2 && enableSave
                            ? async () => {
                                const ret = await updateBookingDetails(
                                  props.modalStore,
                                  props.booking,
                                  state,
                                )
                                return ret.isRight()
                              }
                            : null
                        }
                      />
                    </Form.Group>
                  )}
                </Form>
              )
            }}
          />
        )
      }}
    />
  )
}

async function updateBookingDetails(
  modalStore: ModalStore,
  booking: BookingGutsFragment,
  stateOrig: BookingDetailsState,
): Promise<OrError<BookingGutsFragment>> {
  const variables: SetBookingDetailsMutationVariables = {
    id: booking.id,
    details: bookingDetailsStateToBookingDetails(stateOrig),
  }

  // Migrated from BookingUpdatedModal
  return await modalStore.applyModal({
    pending: ["Updating booking", "Updating booking..."],
    ok: () => ["Booking updated", "Your booking has been updated."],
    error: (m) => ["Update failure", m],
    action: async () => {
      const data = await GraphQL.mutate(SetBookingDetails, variables, {
        refetchQueries: refetchQueries(stateOrig.locale.id, {
          forBookerId: booking.booker && booking.booker.id,
        }),
      })
      return errorUnionToEither(data.setBookingDetails)
    },
  })
}



// WEBPACK FOOTER //
// ./src/components/UpdateBookingWizard.tsx